import React, { useCallback, useEffect, useState } from 'react'
import { CButton, CListGroup, CListGroupItem, CTooltip } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilCloudDownload, cilX } from '@coreui/icons'
import { api } from '../api/client'

export interface AvisoNotif {
  id: string
  tipo: string
  titulo: string
  fecha?: string
  url?: string
  /** Si viene: la notificación ofrece botón Descargar (ej. APK nueva versión). */
  descargar?: string
}

const KEY_DESCARTADAS = 'siprinia.notif.descartadas'

const leerDescartadas = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY_DESCARTADAS) ?? '[]') as string[]
  } catch {
    return []
  }
}

/**
 * [RGM] 2026-10-06 (pedido Rafael: "notificación con fecha y hora de la
 * actualización"): la fecha del aviso se muestra con HORA cuando el origen
 * trae una hora real (la nueva versión del APK = Last-Modified del archivo);
 * los avisos con fecha de día (00:00) quedan solo con la fecha.
 */
export function fmtFechaAviso(fecha?: string): string {
  if (!fecha) return ''
  const d = new Date(fecha)
  if (Number.isNaN(d.getTime())) return fecha.slice(0, 10)
  const fechaTxt = d.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const conHora = d.getHours() !== 0 || d.getMinutes() !== 0
  return conHora
    ? `${fechaTxt} ${d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}`
    : fechaTxt
}

/**
 * Los avisos vivos de la empresa (/notificaciones) + el aviso GLOBAL de NUEVA
 * VERSIÓN DEL APK (pedido Rafael 2026-10-01): el id usa el Last-Modified del
 * archivo servido — al publicar un APK nuevo el id cambia y la notificación
 * reaparece para TODAS las empresas, con botón Descargar.
 * El descarte (✕) es local del navegador — opción (a) aprobada por Rafael.
 */
export function useNotificaciones() {
  const [avisos, setAvisos] = useState<AvisoNotif[]>([])
  const [descartadas, setDescartadas] = useState<string[]>(leerDescartadas)

  useEffect(() => {
    api
      .get<{ total: number; avisos: Array<Record<string, unknown>> }>('/notificaciones')
      .then((r) => {
        setAvisos((prev) => [
          ...prev.filter((a) => a.tipo === 'apk'),
          ...(r?.avisos ?? []).map((a) => ({
            id: `${String(a.tipo)}-${String(a.titulo)}-${String(a.fecha ?? '')}`,
            tipo: String(a.tipo ?? ''),
            titulo: String(a.titulo ?? ''),
            fecha: a.fecha ? String(a.fecha) : undefined,
            url: a.url ? String(a.url) : undefined,
          })),
        ])
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    // Aviso GLOBAL de nueva versión de la app (el APK se sirve junto al sitio).
    fetch('/siprinia-dev.apk', { method: 'HEAD' })
      .then((r) => {
        if (!r.ok) return
        const lm = r.headers.get('Last-Modified') ?? ''
        if (!lm) return
        setAvisos((prev) => [
          ...prev.filter((a) => a.tipo !== 'apk'),
          {
            id: `apk-${lm}`,
            tipo: 'apk',
            // [RGM] 2026-10-05 (pedido Rafael): texto descriptivo "como antes"
            // (Nueva versión de la app disponible) + fecha de publicación
            // (Last-Modified del APK = fecha del archivo servido, hoy) + botón
            // Descargar con icono. La campana y la lista muestran la fecha.
            // Fecha tolerante (review GGA 2026-10-05): un Last-Modified inválido
            // no debe tirar el aviso — sin fecha válida, se omite.
            titulo: 'Nueva versión de la app disponible',
            fecha: Number.isNaN(new Date(lm).getTime()) ? undefined : new Date(lm).toISOString(),
            descargar: '/siprinia-dev.apk',
          },
        ])
      })
      .catch(() => {})
  }, [])

  const descartar = useCallback((id: string) => {
    setDescartadas((prev) => {
      const next = [...new Set([...prev, id])]
      localStorage.setItem(KEY_DESCARTADAS, JSON.stringify(next))
      return next
    })
  }, [])

  return { avisos: avisos.filter((a) => !descartadas.includes(a.id)), descartar }
}

/**
 * Lista de notificaciones (tab "Notificaciones" de Mi Cuenta): título + fecha,
 * ✕ para descartar, y botón DESCARGAR cuando la notificación trae archivo
 * (nueva versión del APK).
 */
export const NotificacionesLista: React.FC<{
  avisos: AvisoNotif[]
  descartar: (id: string) => void
  onIr: (a: AvisoNotif) => void
  descargar?: (a: AvisoNotif) => void
}> = ({ avisos, descartar, onIr, descargar }) => {
  if (avisos.length === 0) {
    return <div className="text-body-secondary">Sin novedades.</div>
  }
  return (
    <CListGroup>
      {avisos.map((a) => (
        <CListGroupItem key={a.id} className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-link p-0 text-start flex-grow-1 text-decoration-none"
            onClick={() => onIr(a)}
          >
            <div className="fw-semibold">{a.titulo}</div>
            {a.fecha && <div className="small text-body-secondary">{fmtFechaAviso(a.fecha)}</div>}
          </button>
          {a.descargar && (
            <CButton
              color="primary"
              size="sm"
              onClick={() => (descargar ? descargar(a) : (window.location.href = a.descargar!))}
            >
              <CIcon icon={cilCloudDownload} className="me-1" /> Descargar
            </CButton>
          )}
          <CTooltip content="Descartar" placement="top">
            <CButton
              color="link"
              size="sm"
              className="p-1 text-body-secondary"
              onClick={() => descartar(a.id)}
              aria-label="Descartar notificación"
            >
              <CIcon icon={cilX} />
            </CButton>
          </CTooltip>
        </CListGroupItem>
      ))}
    </CListGroup>
  )
}
